package fr.manyverse.app.presentation.components.player_screen.character.choice

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.TableCharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.select
import manyverse.shared.generated.resources.select_character
import manyverse.shared.generated.resources.select_character_for_table
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SelectCharacterDialog(
    characters: List<Character>,
    viewModel: TableCharacterViewModel,
    onDismiss: () -> Unit
) {
    var selectedCharacter by remember { mutableStateOf<Character?>(null) }
    var expanded by remember { mutableStateOf(false) }

    val assignState by viewModel.assignState.collectAsStateWithLifecycle()
    val isLoading = assignState is AsyncUiState.Loading

    LaunchedEffect(assignState) {
        if (assignState is AsyncUiState.Success) {
            viewModel.resetAssignState()
            onDismiss()
        }
    }

    AlertDialog(
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.select_character_for_table)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                ExposedDropdownMenuBox(
                    expanded = expanded && !isLoading,
                    onExpandedChange = { expanded = !expanded && !isLoading }
                ) {
                    OutlinedTextField(
                        value = selectedCharacter?.name ?: "",
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(stringResource(Res.string.select_character)) },
                        trailingIcon = {
                            ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded)
                        },
                        colors = ExposedDropdownMenuDefaults.outlinedTextFieldColors(),
                        modifier = Modifier
                            .fillMaxWidth()
                            .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryEditable, true),
                        enabled = !isLoading
                    )
                    ExposedDropdownMenu(
                        expanded = expanded,
                        onDismissRequest = { expanded = false }
                    ) {
                        characters.forEach { character ->
                            DropdownMenuItem(
                                text = { Text(character.name) },
                                onClick = {
                                    selectedCharacter = character
                                    expanded = false
                                }
                            )
                        }
                    }
                }

                if (assignState is AsyncUiState.Error) {
                    Text(
                        text = (assignState as AsyncUiState.Error).message,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { selectedCharacter?.let { viewModel.assignExistingCharacter(it) } },
                enabled = selectedCharacter != null && !isLoading
            ) {
                Text(stringResource(Res.string.select))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !isLoading) {
                Text(stringResource(Res.string.cancel))
            }
        }
    )
}
